import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";
import { Link } from "react-router-dom";

export default function Refund() {
  return (
    <MarketingShell>
      <SEO
        title="Refund & Cancellation Policy — TAXINPHO"
        description="7-day no-questions-asked refund on TAXINPHO subscriptions. Cancel anytime from your Billing page. Pro-rata refunds on yearly plans, fast 5-7 day processing. GST is non-refundable."
        canonical="https://taxinpho.co/refund"
      />
      <article className="max-w-3xl mx-auto px-5 py-16 md:py-20">
        <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-2">Legal</p>
        <h1 className="font-display font-black text-5xl tracking-tighter mb-3">
          Refund &amp; Cancellation Policy
        </h1>
        <p className="text-sm text-zinc-500 mb-8">Last updated: 29 June 2026</p>

        <Section title="1. Free Trial">
          Every new account gets a 7-day free trial with full feature access. No
          credit card is required to start the trial. You may cancel at any time
          during the trial — no charges will be made and no questions asked.
        </Section>

        <Section title="2. Refund Window">
          <p>If you subscribe to a paid plan (Monthly or Yearly) and decide it is not
          right for you, you can request a full refund within <strong>7 calendar days</strong>
          of payment, no questions asked.</p>
          <p>To request a refund, email
          <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline mx-1">taxinpho@gmail.com</a>
          from your registered email address with your payment reference number. Refunds are processed within
          <strong> 5–7 working days</strong> back to the original payment method.</p>
        </Section>

        <Section title="3. No Refund After 7 Days">
          After the 7-day window, refunds are not provided for the current billing
          period. You may still cancel your subscription from the Billing page — your
          access will continue until the end of the period you have already paid for,
          and no further charges will be made.
        </Section>

        <Section title="4. Annual Subscription — Pro-rata Refund Exception">
          For annual subscriptions cancelled after the 7-day refund window, we offer a
          one-time goodwill pro-rata refund of unused months at our discretion. To
          request, email <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline">taxinpho@gmail.com</a> with a brief reason. We typically approve within 5 working days.
        </Section>

        <Section title="5. How to Cancel">
          <p>You can cancel your subscription anytime in two ways:</p>
          <ol className="list-decimal list-inside space-y-1 ml-3">
            <li>Open <Link to="/login" className="text-[#002FA7] underline">TAXINPHO</Link> → Billing page → click <strong>Cancel subscription</strong></li>
            <li>Or email <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline">taxinpho@gmail.com</a> from your registered email address</li>
          </ol>
          <p>Cancellation takes effect at the end of your current billing period.
          You retain full access until then. Your data is preserved for 30 days after
          cancellation in case you wish to reactivate.</p>
        </Section>

        <Section title="6. Failed Payments">
          If your subscription auto-renewal fails (e.g., card expired, insufficient
          funds), we will retry the payment up to 3 times over 7 days and notify you by
          email. If payment still cannot be collected, your account will be downgraded
          to read-only mode for 14 days, and then deactivated. Your data is not deleted
          during this period and can be restored once payment is made.
        </Section>

        <Section title="7. Service Disruption">
          If TAXINPHO experiences a sustained outage of more than 24 consecutive hours
          due to issues attributable solely to us (excluding force majeure, planned
          maintenance, or third-party outages), you are eligible for a pro-rata credit
          for the affected period. Request via
          <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline mx-1">taxinpho@gmail.com</a>.
        </Section>

        <Section title="8. Chargebacks">
          We strongly prefer that you contact us at
          <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline mx-1">taxinpho@gmail.com</a>
          before raising a chargeback with your bank or payment provider. We aim to resolve disputes
          within 48 hours. Unjustified chargebacks may result in immediate account
          suspension and recovery of fees.
        </Section>

        <Section title="9. Taxes (GST)">
          All payments are subject to 18% Goods and Services Tax (GST). GST is
          non-refundable as it is collected on behalf of the government. Refunds, when
          issued, are made on the pre-tax amount.
        </Section>

        <Section title="10. Contact">
          For any billing, refund, or cancellation queries:
          <br /><strong>Email:</strong> <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline">taxinpho@gmail.com</a>
          <br /><strong>Phone:</strong> +91 95564 72222 / +91 88950 06864 (Mon–Sat, 10 AM – 7 PM IST)
          <br /><strong>Address:</strong> TAXINPHO, Shree Krishna Kutir, Shree Ram Colony, Thikadar Para, Balangir, Odisha - 767001, India
        </Section>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display font-bold text-xl tracking-tight mb-3 text-zinc-900">{title}</h2>
      <div className="text-zinc-700 text-sm leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}
